import { useDeepCompareMemo } from 'use-deep-compare';
import { useDuckDbQuery } from '../third-party-kit/hooks/useDuckDbQuery';

export type DuckQueryReturnListTypeWithForceUpdate<T> = {
  isLoading: boolean;
  error: Error | null;
  data: T[] | null;
  query: string | null;
  forceUpdate: () => void;
};

/**
 * hook that handles the execution of a given DuckDB query.
 *
 * @param {string | null} query - The SQL query to be executed.
 * @param {boolean | undefined} skip - Condition to skip query execution.
 *
 * @returns {DuckQueryReturnType<T>} - Returns an array of query results if successful, or null otherwise.
 */
export const useDuckQuery = <T>(
  query: string | null,
  skip: boolean | undefined = false
): DuckQueryReturnListTypeWithForceUpdate<T> => {
  const { arrow, loading, error, forceUpdate } = useDuckDbQuery(
    query || undefined,
    skip
  );

  const result = useDeepCompareMemo(() => {
    if (skip)
      return {
        isLoading: loading,
        error: error || null,
        data: null,
        query,
        forceUpdate,
      };

    if (loading)
      return {
        isLoading: loading,
        error: null,
        data: null,
        query,
        forceUpdate,
      };

    if (error) {
      console.error(error);

      return {
        isLoading: loading,
        error: error || null,
        data: null,
        query,
        forceUpdate,
      };
    }

    if (arrow)
      return {
        isLoading: loading,
        error: error || null,
        data: arrow.toArray().map((row) => row.toJSON()),
        query,
        forceUpdate,
      };

    return {
      isLoading: loading,
      error: error || null,
      data: null,
      query,
      forceUpdate,
    };
  }, [loading, error, arrow, skip]);

  return result as DuckQueryReturnListTypeWithForceUpdate<T>;
};
